import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { WorkflowsService, type WorkflowRead, type WorkflowWrite } from '../client'
import Spinner from '../components/chat/Spinner'
import Modal from '../components/ui/Modal'
import RunLogDialog from '../components/workflows/RunLogDialog'
import RunWorkflowDialog from '../components/workflows/RunWorkflowDialog'
import ScheduleDialog from '../components/workflows/ScheduleDialog'
import WorkflowEditor from '../components/workflows/WorkflowEditor'
import { apiErrorMessage } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { describeSchedule } from '../lib/schedules'
import { useAuth } from '../providers/auth/useAuth'
import { useChat } from '../providers/chat/context'

const GHOST =
  'flex h-10 items-center gap-1 rounded-lg px-2.5 text-sm text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 sm:h-8 sm:text-xs'

function toWrite({ name, description, inputs, steps }: WorkflowRead): WorkflowWrite {
  return { name, description, inputs, steps: steps.map(({ title, detail, tools }) => ({ title, detail, tools })) }
}

export default function Workflows() {
  const { brand } = useAuth()
  const { busy, runWorkflow } = useChat()
  const navigate = useNavigate()
  const [workflows, setWorkflows] = useState<WorkflowRead[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<WorkflowRead | null>(null)
  const [running, setRunning] = useState<WorkflowRead | null>(null)
  const [deleting, setDeleting] = useState<WorkflowRead | null>(null)
  const [removing, setRemoving] = useState(false)
  const [scheduling, setScheduling] = useState<WorkflowRead | null>(null)
  const [logging, setLogging] = useState<WorkflowRead | null>(null)
  const brandId = brand?.id

  useEffect(() => {
    if (!brandId) return
    WorkflowsService.listWorkflows(brandId)
      .then((list) => setWorkflows(list.workflows))
      .catch((err: unknown) => {
        setWorkflows([])
        setError(apiErrorMessage(err))
      })
  }, [brandId])

  async function save(workflow: WorkflowWrite) {
    if (!brandId || !editing) return
    const updated = await WorkflowsService.replaceWorkflow(editing.id, brandId, workflow)
    setWorkflows((current) => [
      { ...updated, schedule: editing.schedule },
      ...(current ?? []).filter(({ id }) => id !== updated.id),
    ])
    setEditing(null)
  }

  function scheduled(workflowId: string, schedule: WorkflowRead['schedule']) {
    setWorkflows((current) => (current ?? []).map((w) => (w.id === workflowId ? { ...w, schedule } : w)))
    setScheduling(null)
  }

  async function remove() {
    if (!brandId || !deleting) return
    setRemoving(true)
    try {
      await WorkflowsService.deleteWorkflow(deleting.id, brandId)
      setWorkflows((current) => (current ?? []).filter(({ id }) => id !== deleting.id))
      setDeleting(null)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setRemoving(false)
    }
  }

  async function run(workflow: WorkflowRead, inputs: Record<string, string>) {
    setRunning(null)
    try {
      const threadId = await runWorkflow(workflow, inputs)
      if (threadId) void navigate(`/c/${threadId}`)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <header className="border-b border-stone-200 px-4 py-4 sm:px-6">
        <h1 className="text-base font-medium text-stone-900">Workflows</h1>
        <p className="mt-0.5 text-sm text-stone-500">
          Plans you've saved to run again. Finish a plan in chat, then choose "Save as workflow" on its card.
        </p>
      </header>

      <section className="px-4 py-4 sm:px-6">
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        {workflows === null && !error && (
          <div className="flex justify-center py-12">
            <Spinner className="size-5 border-purple-200 border-t-purple-700" />
          </div>
        )}

        {workflows?.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <span className="flex size-10 items-center justify-center rounded-lg bg-stone-100">
              <ClipboardDocumentListIcon className="size-5 text-purple-700" />
            </span>
            <p className="text-sm font-medium text-stone-900">No workflows yet</p>
            <p className="max-w-sm text-sm text-stone-500">
              Turn on Plan in the chat, run a plan, and save it here to run again with new inputs.
            </p>
          </div>
        )}

        {workflows && workflows.length > 0 && (
          <ul className="grid gap-3 md:grid-cols-2">
            {workflows.map((workflow) => (
              <li
                key={workflow.id}
                className="flex flex-col rounded-xl border border-stone-200 bg-white p-4 shadow-[0_1px_2px_rgba(28,25,23,0.06)]"
              >
                <p className="text-sm font-medium text-stone-900">{workflow.name}</p>
                {workflow.description && <p className="mt-1 text-sm text-stone-500">{workflow.description}</p>}
                <p className="mt-2 text-xs text-stone-500">
                  {workflow.steps.length} step{workflow.steps.length === 1 ? '' : 's'}
                </p>
                {workflow.schedule && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-stone-600">
                    <CalendarDaysIcon className="mt-px size-3.5 shrink-0 text-purple-700" />
                    <span>
                      {workflow.schedule.enabled ? describeSchedule(workflow.schedule) : 'Schedule paused'}
                      {workflow.schedule.enabled && workflow.schedule.next_run_at && (
                        <span className="block text-stone-500">Next: {formatDateTime(workflow.schedule.next_run_at)}</span>
                      )}
                    </span>
                  </p>
                )}
                {workflow.inputs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {workflow.inputs.map((input) => (
                      <span key={input.name} className="rounded-md bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-600">
                        {input.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-1 border-t border-stone-100 pt-3">
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => setRunning(workflow)}
                    className="flex h-10 items-center gap-1 rounded-lg bg-purple-700 px-3 text-sm font-medium text-white transition enabled:hover:bg-purple-800 disabled:opacity-50 sm:h-8 sm:text-xs"
                  >
                    <PlayIcon className="size-3.5" />
                    Run
                  </button>
                  <button type="button" onClick={() => setScheduling(workflow)} className={GHOST}>
                    <CalendarDaysIcon className="size-3.5" />
                    Schedule
                  </button>
                  <button type="button" onClick={() => setLogging(workflow)} className={GHOST}>
                    <ClockIcon className="size-3.5" />
                    Runs
                  </button>
                  <button type="button" onClick={() => setEditing(workflow)} className={GHOST}>
                    <PencilSquareIcon className="size-3.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${workflow.name}`}
                    onClick={() => setDeleting(workflow)}
                    className={`${GHOST} ml-auto hover:text-red-600`}
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <WorkflowEditor
          title="Edit workflow"
          initial={toWrite(editing)}
          saveLabel="Save changes"
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}

      {running && (
        <RunWorkflowDialog
          workflow={running}
          disabled={busy !== null}
          onRun={(inputs) => void run(running, inputs)}
          onClose={() => setRunning(null)}
        />
      )}

      {scheduling && brandId && (
        <ScheduleDialog
          workflow={scheduling}
          brandId={brandId}
          onSaved={(schedule) => scheduled(scheduling.id, schedule)}
          onClose={() => setScheduling(null)}
        />
      )}

      {logging && brandId && <RunLogDialog workflow={logging} brandId={brandId} onClose={() => setLogging(null)} />}

      {deleting && (
        <Modal
          title="Delete workflow?"
          width="max-w-md"
          onClose={() => setDeleting(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                className="h-10 rounded-lg px-3 text-sm text-stone-600 transition hover:bg-stone-100 sm:h-8"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={removing}
                onClick={() => void remove()}
                className="h-10 rounded-lg bg-red-600 px-3 text-sm font-medium text-white transition enabled:hover:bg-red-700 disabled:opacity-50 sm:h-8"
              >
                {removing ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          }
        >
          <p className="text-sm text-stone-600">
            "{deleting.name}" will be removed, with its schedule and run log. Chats it already ran stay as they are.
          </p>
        </Modal>
      )}
    </div>
  )
}
